import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';

/**
 * FilterDropdown - Custom dropdown component replacing native <select>
 * Features: click-to-open panel, option list, active state indicator,
 * dark mode support, chevron icon, click-outside-to-close, keyboard nav
 */
const FilterDropdown = ({
  label = '',
  value = 'all',
  onChange,
  options = [],
  placeholder = 'All',
  className = '',
}) => {
  const { settings } = useSettings();
  const { isDark } = useTheme();
  const primaryColor = settings?.theme?.colors?.primary || '#3B5BDB';
  const accentColor = settings?.theme?.colors?.accent || '#845EF7';

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const [focusIndex, setFocusIndex] = useState(-1);

  // Find the display label for the current value
  const selectedOption = options.find(opt =>
    (typeof opt === 'object' ? opt.value : opt) === value
  );
  const displayText = value === 'all'
    ? placeholder
    : typeof selectedOption === 'object'
      ? selectedOption.label
      : selectedOption || value;

  const isActive = value !== 'all';

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setFocusIndex(-1);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Scroll focused item into view
  useEffect(() => {
    if (isOpen && focusIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-dropdown-item]');
      if (items[focusIndex]) {
        items[focusIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [focusIndex, isOpen]);

  const allOptions = [
    { value: 'all', label: placeholder },
    ...options.map(opt =>
      typeof opt === 'object' ? opt : { value: opt, label: opt }
    ),
  ];

  const handleSelect = useCallback((optValue) => {
    onChange(optValue);
    setIsOpen(false);
    setFocusIndex(-1);
  }, [onChange]);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setFocusIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusIndex(prev => Math.min(prev + 1, allOptions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusIndex >= 0 && focusIndex < allOptions.length) {
          handleSelect(allOptions[focusIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setFocusIndex(-1);
        break;
      default:
        break;
    }
  }, [isOpen, focusIndex, allOptions, handleSelect]);

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
          {label}
        </label>
      )}

      {/* Gradient border wrapper */}
      <div
        className="rounded-xl p-[1.5px] transition-all duration-200"
        style={{
          background: (isOpen || isActive)
            ? `linear-gradient(135deg, ${primaryColor}, ${accentColor})`
            : isDark ? '#374151' : '#e5e7eb',
        }}
      >
        {/* Trigger button */}
        <button
          type="button"
          onClick={() => {
            setIsOpen(!isOpen);
            if (!isOpen) setFocusIndex(allOptions.findIndex(o => o.value === value));
          }}
          onKeyDown={handleKeyDown}
          className="w-full flex items-center justify-between pl-4 pr-3 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-[10px] text-sm transition-all outline-none cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/60"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {isActive && (
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: primaryColor }}
            />
          )}
          <span className={`truncate ${isActive ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
            {displayText}
          </span>
        </div>

        {/* Chevron icon */}
        <svg
          className={`w-4 h-4 flex-shrink-0 ml-2 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        </button>
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <div
          className="absolute z-50 mt-1.5 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden"
          style={{
            animation: 'dropdownSlide 0.15s ease-out',
          }}
        >
          <ul
            ref={listRef}
            role="listbox"
            className="max-h-56 overflow-y-auto py-1 custom-scrollbar"
          >
            {allOptions.map((opt, index) => {
              const isSelected = opt.value === value;
              const isFocused = index === focusIndex;

              return (
                <li
                  key={opt.value}
                  data-dropdown-item
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(opt.value)}
                  onMouseEnter={() => setFocusIndex(index)}
                  className={`
                    flex items-center gap-2.5 px-4 py-2.5 text-sm cursor-pointer transition-colors
                    ${isFocused ? 'bg-gray-100 dark:bg-gray-700/60' : ''}
                    ${isSelected ? 'font-medium' : ''}
                  `}
                  style={isSelected ? { color: primaryColor } : {}}
                >
                  {/* Check icon for selected */}
                  <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                    {isSelected && (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        style={{ color: primaryColor }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>

                  <span className={`truncate ${isSelected ? '' : 'text-gray-700 dark:text-gray-300'}`}>
                    {opt.label}
                  </span>
                </li>
              );
            })}
          </ul>

          {/* Animation keyframes */}
          <style>{`
            @keyframes dropdownSlide {
              from { opacity: 0; transform: translateY(-4px); }
              to { opacity: 1; transform: translateY(0); }
            }
            .custom-scrollbar::-webkit-scrollbar { width: 6px; }
            .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
            .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(156, 163, 175, 0.4); border-radius: 3px; }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(156, 163, 175, 0.6); }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;
